import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTAFooter from "@/components/CTAFooter";
import { Check, Clock, DollarSign, CreditCard, Smartphone, RefreshCw, ShieldCheck, CalendarCheck, Banknote, ArrowRight } from "lucide-react";

const benefits = [
  {
    icon: CalendarCheck,
    title: "Flexible Repayment Terms",
    subtitle: "Up to 60 months to repay",
    description: "Choose a repayment schedule that suits your budget with terms ranging from 6 to 60 months.",
  },
  {
    icon: Clock,
    title: "Rapid Approval Process",
    subtitle: "Get an outcome in minutes",
    description: "Whether applying for pre-approval or after receiving a quote, your application is processed swiftly.",
  },
  {
    icon: DollarSign,
    title: "Finance from $1,000 to $30,000",
    subtitle: "Fixed management fees",
    description: "Management fees remain the same regardless of the finance amount or term you select.",
  },
  {
    icon: RefreshCw,
    title: "Account Reuse Available",
    subtitle: "Reuse repaid amounts",
    description: "With an active 0% interest plan, you may reuse amounts already repaid or request a limit increase.",
  },
  {
    icon: Smartphone,
    title: "Track Your Repayments",
    subtitle: "Partner app integration",
    description: "Monitor all repayments conveniently through any of our partner lender apps at no cost.",
  },
  {
    icon: Banknote,
    title: "Additional Repayments Welcome",
    subtitle: "Pay it off faster",
    description: "Make extra repayments at any time with no additional fees — giving you full control over your plan.",
  },
];

const transparencyCards = [
  {
    icon: ShieldCheck,
    title: "No Deposit",
    subtitle: "Up to 100% finance available",
    description: "Purchase everything you need today without waiting or saving for a deposit.",
  },
  {
    icon: DollarSign,
    title: "No Early Payout Fees",
    subtitle: "Zero additional charges",
    description: "Settle your balance ahead of schedule with absolutely no penalties or hidden costs.",
  },
  {
    icon: CalendarCheck,
    title: "Affordable Repayments",
    subtitle: "Flexible fortnightly schedule",
    description: "Automatic repayments set to dates that work for you, making budgeting simple.",
  },
  {
    icon: Smartphone,
    title: "Free Partner Apps",
    subtitle: "Brighte, Zip Pay & Humm",
    description: "Download, access and use all partner apps for free to track and manage your payments.",
  },
];

const FinanceOptions = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageSEO title="Interest-Free AC Finance Options | ACG Southern Highlands" description="Pay for your air conditioning over time with interest-free finance. Flexible terms up to 60 months via Brighte, Zip Pay and Humm. Apply online today." />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Finance Options" }]} />

      {/* Hero Section */}
      <section className="relative bg-acg-dark py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-acg-charcoal via-acg-dark to-acg-charcoal" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <div className="w-8 h-8 border-t-4 border-l-4 border-secondary mb-6" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-primary-foreground leading-tight mb-4">
                Finance Options
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-secondary mb-6">
                Fully Licensed, Interest-Free Payments Available
              </p>
              <ul className="space-y-3 mb-8">
                {["Free No-Obligation Onsite Quotation", "Same Day Installations Available", "Servicing All of the Southern Highlands"].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-primary-foreground/80">
                    <Check className="w-5 h-5 text-secondary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://brighte.com.au/payment-plans/interest-free"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-8 py-4 rounded hover:opacity-90 transition-opacity uppercase text-sm tracking-wider"
              >
                Apply Now
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="lg:w-1/2 flex justify-center lg:justify-end">
              <img
                src="/src/assets/brighte-card.png"
                alt="ACG Brighte interest-free payment card — finance your air conditioning with zero interest"
                className="w-full max-w-md drop-shadow-2xl"
                width={448}
                height={280}
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Best Lenders Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-foreground mb-4">
            The Best Lenders in the Market
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-10">
            We partner with Australia's most trusted lenders to provide flexible, transparent finance options for every budget.
          </p>
          
          {/* Large Partner Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-6 mb-10">
            <a
              href="https://brighte.com.au/payment-plans/interest-free"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border-2 border-[#00A651] text-[#00A651] font-black text-xl px-12 py-6 rounded-xl hover:bg-[#00A651] hover:text-white transition-all shadow-lg hover:shadow-xl"
            >
              Brighte
            </a>
            <a
              href="https://zip.co/au"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border-2 border-[#2D8CFF] text-[#2D8CFF] font-black text-xl px-12 py-6 rounded-xl hover:bg-[#2D8CFF] hover:text-white transition-all shadow-lg hover:shadow-xl"
            >
              Zip Pay
            </a>
            <a
              href="https://www.shophumm.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border-2 border-[#FF6B00] text-[#FF6B00] font-black text-xl px-12 py-6 rounded-xl hover:bg-[#FF6B00] hover:text-white transition-all shadow-lg hover:shadow-xl"
            >
              Humm
            </a>
            <a
              href="https://www.shophumm.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border-2 border-[#FF6B00] text-[#FF6B00] font-black text-xl px-12 py-6 rounded-xl hover:bg-[#FF6B00] hover:text-white transition-all shadow-lg hover:shadow-xl"
            >
              Humm<span className="text-sm align-super">90</span>
            </a>
          </div>
          
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Already have an account with any of our payment partners? Applying and moving your loans will be even easier!
          </p>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-8 bg-acg-dark">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:divide-x divide-primary-foreground/20">
            {[
              { label: "100% Interest Free", sub: "No hidden charges" },
              { label: "5 Minute Approvals", sub: "Fast & simple process" },
              { label: "All Major Lenders", sub: "Top providers in Australia" },
            ].map((item) => (
              <div key={item.label} className="text-center py-6 px-4">
                <p className="text-xl font-black text-primary-foreground">{item.label}</p>
                <p className="text-sm text-primary-foreground/60">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-foreground mb-3">
              Only the <span className="text-primary">Best Options</span> for Our Clients
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Interest-free payment plans designed with our clients in mind
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="relative">
                <img
                  src="/src/assets/card-corner-frame.png"
                  alt=""
                  className="absolute -top-2 -left-2 w-8 h-8 z-10"
                />
                <div className="bg-card border border-border rounded-lg p-8 hover:shadow-lg hover:border-primary/30 transition-all group">
                  <h3 className="text-lg font-bold text-foreground mb-1">{benefit.title}</h3>
                  <p className="text-sm font-semibold text-secondary mb-3">{benefit.subtitle}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technician Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <img
            src="/src/assets/finance-technician.png"
            alt="ACG licensed technician installing air conditioning — interest-free payment plans available for all clients"
            className="w-full max-w-6xl mx-auto rounded-lg shadow-2xl"
            width={1200}
            height={600}
            loading="lazy"
          />
        </div>
      </section>

      {/* Convenience & Transparency */}
      <section className="py-20 bg-acg-charcoal">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-primary-foreground mb-3">
              Convenience & Transparency
            </h2>
            <p className="text-primary-foreground/60 max-w-lg mx-auto">
              Peace of mind with every payment — no surprises, no stress
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {transparencyCards.map((card) => (
              <div
                key={card.title}
                className="bg-accent/50 border border-primary-foreground/10 rounded-lg p-8 text-center hover:border-secondary/40 transition-all"
              >
                <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-5">
                  <card.icon className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-primary-foreground mb-1">{card.title}</h3>
                <p className="text-sm font-semibold text-secondary mb-3">{card.subtitle}</p>
                <p className="text-sm text-primary-foreground/60 leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Understanding Finance & Costs */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-black text-foreground mb-6">How AC Finance Enquiries Work</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Financing a new heating and cooling system doesn't need to be complicated. After we've provided your free, no-obligation on-site quote, you can choose to apply for finance through one of our lending partners — Brighte, Zip Pay or Humm. Applications are completed directly with the lender, who will assess your eligibility, term and repayment schedule based on their own criteria. We don't set or influence interest rates, fees or approval decisions; our role is to give you an accurate quote for the installed system so you know exactly what you're financing.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            If you're pre-approved before getting a quote, that's fine too — simply let us know your approved limit and we can help you select a system that fits comfortably within it. Either way, we always recommend reading the lender's current terms and conditions carefully, as rates, fees and eligibility criteria can change and are set independently by each finance provider.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">What Affects the Cost of a System</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Every Southern Highlands home is different, and several factors influence what a heating-focused installation will cost, including:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
            <li>The number of rooms or zones you want heated and cooled, and whether it's a single split or a full ducted system</li>
            <li>The heating capacity required to cope with your property's elevation, exposure and typical winter minimums</li>
            <li>Existing infrastructure — whether ductwork, wiring or an outdoor unit pad already exists, or needs to be installed</li>
            <li>Brand and model selection, including low-ambient heating performance and warranty length</li>
            <li>Access and installation complexity, such as roof space, two-storey layouts or difficult site access</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Because of these variables, we don't publish fixed prices online — every quote is based on an on-site assessment so the figure you receive reflects your actual home, not a generic estimate.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Budgeting for a Heating-Focused Install</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            In a cold-climate region like the Southern Highlands, it's worth budgeting for a system sized to handle genuine winter heating loads, not just summer comfort. A unit that's undersized for our frosts may run constantly and struggle to reach temperature, while a correctly sized system runs more efficiently and comfortably through the coldest months. Spreading the cost over an interest-free payment plan can make it more manageable to invest in the right-sized system up front, rather than compromising on capacity to fit a smaller initial budget.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Factors That Change the Final Price</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Beyond the core system and installation, a few less obvious factors can move your final figure up or down. Switchboard capacity is one — some older Southern Highlands homes need a circuit upgrade before a new outdoor unit can be safely connected, particularly on properties that haven't had electrical work done in decades. Roof and ceiling access is another: a steep or restricted roof cavity can add time to a ducted installation, while a straightforward single-storey home with good access is generally quicker and more cost-effective to fit out.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Removal and disposal of an old system, extending existing ductwork to a new room, relocating an awkwardly placed outdoor unit, and any make-good work to plaster, brickwork or cladding after installation can all be factors too. None of these are unusual — our technicians see them regularly across the Highlands — but they're best identified during your on-site quote rather than assumed away, which is another reason we don't offer blind phone quotes for new installations.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Staged Upgrades: Doing It in Steps</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Not every Southern Highlands home needs a full whole-house system installed in one go. Many of our clients choose a staged approach — starting with a single split system in the main living area or the coldest bedroom, then adding further zones or upgrading to a ducted system over subsequent years as budget allows. This can be a sensible way to address the most urgent heating gap first, while planning the rest of the property's comfort needs over time.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            If staging your upgrade appeals, mention it when you book your quote. We can advise on sequencing — for example, whether it makes sense to run additional wiring or a larger outdoor unit pad now, even if the second zone isn't installed until later, to avoid unnecessary rework. Interest-free finance can be applied to each stage separately, so you're not required to commit to the entire project's cost upfront.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Thinking About Energy-Cost Payback</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            A common question we hear is whether upgrading an old, inefficient system is worth it before it has completely failed. While we can't quote specific savings figures without knowing your current system, usage patterns and electricity plan, the general principle is straightforward: an ageing, non-inverter unit typically has to work harder and run longer to reach the same temperature as a modern, correctly sized inverter system, especially through a Southern Highlands winter. Over the life of the system, more efficient heating can offset some of the upfront cost through lower running costs — though the actual payback period depends heavily on your specific circumstances, so we'd encourage you to view efficiency as one factor among several, not a guaranteed dollar figure.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            When comparing options during your quote, ask us about the difference in energy star ratings and heating performance between models — this gives you a more useful basis for weighing upfront cost against ongoing running costs than price alone.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Preparing for a Finance Enquiry</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            To keep things moving quickly once you've received your on-site quote, it helps to have a few things ready before you apply with Brighte, Zip Pay or Humm: proof of identity, your income and employment details, and an understanding of the approximate amount you'd like to finance. Each lender has its own application process and eligibility checks, so we'd recommend reviewing their current requirements on their website beforehand.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            It's also worth deciding in advance roughly how many months you'd like to repay over, as this affects your fortnightly or monthly repayment amount. If you're not sure what suits your budget, our team can talk through the general shape of different term lengths once your quote is finalised — while the lender confirms the specific numbers that apply to your application.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Finance FAQ</h2>
          <div className="space-y-6">
            {[
              { q: "Do you charge more if I pay with finance?", a: "No — our quoted price for the installed system is the same regardless of how you choose to pay. Any fees associated with a finance plan are charged by the lending partner, not by ACG." },
              { q: "Can I get pre-approved before booking a quote?", a: "Yes, you're welcome to apply for pre-approval with Brighte, Zip Pay or Humm before we visit. This can make it easier to plan your system selection once we provide your on-site quote." },
              { q: "What happens if my finance application isn't approved?", a: "If a lender doesn't approve your application, you can still proceed with a standard payment method, or discuss alternative options with us directly." },
              { q: "Is there a minimum or maximum amount I can finance?", a: "Each lender sets their own minimum and maximum finance amounts and eligibility criteria. Check the current details on the lender's website or speak with them directly during your application." },
              { q: "Will financing affect my warranty?", a: "No. Manufacturer and installation warranties apply in exactly the same way regardless of your payment method." },
              { q: "Can I make extra repayments or pay off early?", a: "Generally yes — our finance partners typically allow additional repayments and early payout with no penalty, but always confirm current terms directly with the lender before applying." },
              { q: "Can I use finance for a staged upgrade rather than a full system at once?", a: "Yes, you can apply for finance separately for each stage of a staged upgrade. This lets you address your most urgent heating need first and finance later stages independently as your plans progress." },
              { q: "Does ACG recommend a specific lender?", a: "We work with Brighte, Zip Pay and Humm and are happy to explain how each generally operates, but we don't advise on which is financially best for your situation — that depends on your personal circumstances, so it's worth comparing their current terms directly." },
              { q: "What documents should I have ready for a finance application?", a: "Typically you'll need proof of identity and income details, though exact requirements are set by each lender and can change, so check their website or application portal for the current list before you begin." },
              { q: "Will applying for finance affect my credit score?", a: "Finance applications are assessed and processed entirely by the lender, and any impact on your credit file depends on their process. Please check directly with Brighte, Zip Pay or Humm for details on how their application process may affect your credit history." },
            ].map((faq) => (
              <div key={faq.q} className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-bold text-foreground mb-2">{faq.q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-primary-foreground mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-primary-foreground/80 max-w-lg mx-auto mb-8">
            Apply in minutes and enjoy interest-free payments on your new air conditioning system.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://brighte.com.au/payment-plans/interest-free"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-foreground text-primary font-bold px-8 py-4 rounded hover:opacity-90 transition-opacity uppercase text-sm tracking-wider"
            >
              Apply Now
            </a>
            <a
              href="tel:0480841485"
              className="border-2 border-primary-foreground text-primary-foreground font-bold px-8 py-4 rounded hover:bg-primary-foreground/10 transition-colors uppercase text-sm tracking-wider"
            >
              Call 0480 841 485
            </a>
          </div>
        </div>
      </section>

      <CTAFooter />
    </div>
  );
};

export default FinanceOptions;
